import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, FileText, ImageOff, Pause, Play, User } from "lucide-react";
import { absoluteMediaUrl } from "../../lib/api";
import { cleanTechnicalMediaText, isOnlyTechnicalMediaText } from "../../utils/formatMessage";
import MediaViewer from "./MediaViewer";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function shouldShowContent(content, hasMedia) {
  if (!content) return false;
  if (!hasMedia) return true;
  return !isOnlyTechnicalMediaText(content);
}

function filenameFromUrl(url) {
  if (!url) return "documento.pdf";
  const clean = String(url).split("?")[0].split("#")[0];
  const last = clean.split("/").filter(Boolean).pop();
  if (!last) return "documento.pdf";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function LinkifiedText({ text, isOut }) {
  const parts = String(text || "").split(/(https?:\/\/[^\s]+)/gi);
  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }
    const trailing = part.match(/[),.;!?]+$/)?.[0] || "";
    const href = trailing ? part.slice(0, -trailing.length) : part;
    return (
      <React.Fragment key={index}>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className={`font-medium underline underline-offset-2 ${isOut ? "text-emerald-100" : "text-sky-300"}`}
        >
          {href}
        </a>
        {trailing}
      </React.Fragment>
    );
  });
}

function AudioBubble({ src, isOut, showContent }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const seek = (event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = Number(event.target.value);
    audio.currentTime = next;
    setCurrent(next);
  };

  const fmt = (value) => {
    if (!Number.isFinite(value) || value <= 0) return "0:00";
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  return (
    <div className={`flex items-center gap-2.5 ${showContent ? "mb-2" : ""}`}>
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isOut ? "bg-emerald-500/20 text-emerald-100" : "bg-slate-600 text-slate-100"}`}>
        <User className="h-4 w-4" />
      </div>
      <button
        type="button"
        onClick={toggle}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition ${isOut ? "bg-emerald-200 text-emerald-950 hover:bg-white" : "bg-slate-200 text-slate-900 hover:bg-white"}`}
        aria-label={playing ? "Pausar audio" : "Reproduzir audio"}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="ml-0.5 h-3.5 w-3.5" />}
      </button>
      <div className="min-w-[150px] flex-1">
        <input
          type="range"
          min="0"
          max={duration || 1}
          value={duration ? Math.min(current, duration) : 0}
          onChange={seek}
          className={`h-1 w-full cursor-pointer accent-current ${isOut ? "text-emerald-200" : "text-[#25D366]"}`}
        />
        <div className={`mt-0.5 text-[10px] ${isOut ? "text-emerald-100/60" : "text-slate-400"}`}>
          {fmt(current)} {duration ? `/ ${fmt(duration)}` : ""}
        </div>
      </div>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
      />
    </div>
  );
}

export default function MessageBubble({ message, index }) {
  const isOut = message.direction === "out";
  const hasMedia = !!message.media_url;
  // Mensagem veio com media_type detectado (imagem/áudio/vídeo/documento)
  // mas sem media_url -- o backend não conseguiu recuperar a mídia (link
  // expirado, mensagem encaminhada de conversa antiga, etc. -- ver Fase B
  // da investigação de mídia perdida do WhatsApp). Em vez de a mensagem
  // "sumir" (sem mídia pra renderizar e sem indicação do que aconteceu),
  // mostramos um indicador claro em vez do balão de mídia normal.
  const isMediaUnavailable = !hasMedia && !!message.media_type;
  const mediaUrl = absoluteMediaUrl(message.media_url);
  const [viewerOpen, setViewerOpen] = useState(false);
  const cleanedContent = cleanTechnicalMediaText(message.content, message.media_type);
  const showContent = shouldShowContent(message.content, hasMedia || isMediaUnavailable) && !!cleanedContent;
  const isImage = message.media_type === "image";
  const isAudio = message.media_type === "audio";
  const isVideo = message.media_type === "video";
  const isDocument = hasMedia && !isImage && !isAudio && !isVideo;
  const compactMedia = (hasMedia || isMediaUnavailable) && !showContent;

  const mediaUnavailableLabel = () => {
    if (isImage) return "Imagem indisponível";
    if (isAudio) return "Áudio indisponível";
    if (isVideo) return "Vídeo indisponível";
    return "Arquivo indisponível";
  };

  const renderMedia = () => {
    if (isMediaUnavailable) {
      return (
        <div
          data-testid="media-unavailable"
          className={`flex items-center gap-2 rounded-md border border-dashed px-2.5 py-2 text-xs ${showContent ? "mb-2" : ""} ${
            isOut ? "border-emerald-100/25 text-emerald-100/80" : "border-slate-600 text-slate-300"
          }`}
        >
          <ImageOff className="h-4 w-4 shrink-0 opacity-70" />
          <div>
            <div className="font-medium">{mediaUnavailableLabel()}</div>
            <div className="opacity-70">Não foi possível carregar (link expirado ou mensagem antiga)</div>
          </div>
        </div>
      );
    }

    if (!hasMedia) return null;

    if (isImage) {
      return (
        <div className={showContent ? "mb-2 overflow-hidden rounded-md" : "overflow-hidden rounded-md"}>
          <button type="button" onClick={() => setViewerOpen(true)} className="block w-full cursor-zoom-in text-left">
            <img
              src={mediaUrl}
              alt={showContent ? cleanedContent : "Imagem"}
              className="max-h-72 w-full max-w-[280px] object-cover transition-transform duration-200 hover:scale-[1.01]"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23333' width='100' height='100'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' fill='%23999' font-size='12'%3EImagem%3C/text%3E%3C/svg%3E";
              }}
            />
          </button>
        </div>
      );
    }

    if (isAudio) {
      return <AudioBubble src={mediaUrl} isOut={isOut} showContent={showContent} />;
    }

    if (isVideo) {
      return (
        <div className={showContent ? "mb-2 overflow-hidden rounded-md bg-black/20" : "overflow-hidden rounded-md bg-black/20"}>
          <video
            controls
            className="max-h-64 w-full max-w-[320px] object-cover"
            controlsList="nodownload"
            preload="metadata"
          >
            <source src={mediaUrl} type="video/mp4" />
            Seu navegador nao suporta video HTML5
          </video>
        </div>
      );
    }

    const documentName = showContent && /\.[a-z0-9]{2,5}$/i.test(cleanedContent)
      ? cleanedContent
      : filenameFromUrl(mediaUrl);
    return (
      <a
        href={mediaUrl}
        target="_blank"
        rel="noreferrer"
        className={`${showContent ? "mb-2" : ""} inline-flex max-w-[280px] items-center gap-2 rounded-md border px-2.5 py-2 text-xs font-medium transition-colors ${
          isOut
            ? "border-[#25D366]/20 bg-[#25D366]/10 text-emerald-100 hover:bg-[#25D366]/20"
            : "border-slate-600/70 bg-slate-700/50 text-slate-100 hover:bg-slate-700"
        }`}
      >
        <FileText className="h-4 w-4" />
        <span className="truncate">{documentName}</span>
        <Download className="ml-1 h-3 w-3" />
      </a>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, delay: Math.min(index * 0.02, 0.2) }}
      className={`flex ${isOut ? "justify-end" : "justify-start"} px-2 py-0.5`}
      data-testid={`message-${message.id}`}
    >
      <div
        className={`${isImage && !showContent ? "w-auto max-w-[292px] p-1" : isAudio && !showContent ? "w-auto max-w-[330px] px-2.5 py-2" : isDocument && !showContent ? "w-auto max-w-[310px] px-2.5 py-2" : isMediaUnavailable && !showContent ? "w-auto max-w-[310px] px-2.5 py-2" : "w-full max-w-[78%] px-3 py-2.5"} rounded-lg text-sm shadow-sm overflow-hidden ${
          isOut
            ? "bg-[#0B3D1F] border border-[#25D366]/10 text-emerald-50 rounded-tr-sm"
            : "bg-slate-800 border border-slate-700/40 text-slate-100 rounded-tl-sm"
        }`}
      >
        {renderMedia()}
        {showContent && (
          <div className="whitespace-pre-wrap break-words leading-snug">
            <LinkifiedText text={cleanedContent} isOut={isOut} />
          </div>
        )}
        <div
          className={`text-[10px] ${compactMedia ? "mt-0.5" : "mt-1"} text-right font-medium ${
            isOut ? "text-emerald-300/60" : "text-slate-500"
          }`}
        >
          {formatTime(message.created_at)}
        </div>
      </div>
      <MediaViewer
        open={viewerOpen}
        src={mediaUrl}
        alt={showContent ? cleanedContent : "Imagem"}
        onClose={() => setViewerOpen(false)}
      />
    </motion.div>
  );
}
